import React, { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import customerAPI from "../api/customerAPI";
import CustomerForm from "./CustomerForm";

const onlyDigits = (s = "") => (s || "").replace(/\D/g, "");

const formatTinDisplay = (tinRaw) => {
  const digits = onlyDigits(tinRaw);
  if (digits.length > 9) {
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 9)}-${digits.slice(9)}`;
  }
  return tinRaw;
};

const CustomerModal = ({ isOpen, onClose, onCreated, onSaved, initialName = "", initialCustomer = null }) => {
  const isEditMode = Boolean(initialCustomer?._id);
  const [saving, setSaving] = useState(false);

  const handleSave = async (payload) => {
    try {
      setSaving(true);
      //console.log('[CustomerModal] handleSave, isEditMode:', isEditMode, 'payload:', payload);

      const res = isEditMode
        ? await customerAPI.update(initialCustomer._id, payload)
        : await customerAPI.create(payload);
      
      console.log('[CustomerModal] API response:', res);
      const created = res?.data || res;
      console.log('[CustomerModal] created:', created);

      if (!created?._id) {
        throw new Error(`Customer ${isEditMode ? "update" : "creation"} failed (no _id returned).`);
      }

      const selectedCustomer = {
        _id: created._id,
        name: created.name,
        email: created.email,
        phone: created.phone,
        mobile: created.mobile,
        address: created.address,
        city: created.city,
        tin: created.tin,
        customerType: created.customerType,
        label: created.email ? `${created.name} (${created.email})` : created.name,
      };

      console.log('[CustomerModal] calling onCreated/onSaved with:', selectedCustomer);
      if (isEditMode) {
        onSaved?.(selectedCustomer);
      } else {
        onCreated?.(selectedCustomer);
      }
      setSaving(false);
      onClose?.();
    } catch (e) {
      console.error('[CustomerModal] handleSave error:', e);
      setSaving(false);
      throw e;
    }
  };

  if (!isOpen) return null;

  if (typeof document === "undefined") return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center p-4"
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div
        className="absolute inset-0 bg-black/40"
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.stopPropagation();
          onClose?.();
        }}
      />

      <div
        className="relative w-full max-w-xl bg-white rounded-lg shadow-xl border border-gray-200 max-h-screen overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b">
          <div className="text-lg font-semibold text-gray-800">{isEditMode ? "Edit Customer" : "Create Customer"}</div>
          <div className="text-xs text-gray-500 mt-1">
            {isEditMode
              ? "Update customer details."
              : "Add a customer to your masterlist."}
          </div>
        </div>

        <div className="p-5">
          <CustomerModalForm
            initialCustomer={initialCustomer}
            initialName={initialName}
            onSave={handleSave}
            onCancel={onClose}
            saving={saving}
          />
        </div>
      </div>
    </div>,
    document.body
  );
};

// Separate form component for the modal
const CustomerModalForm = ({ initialCustomer, initialName, onSave, onCancel, saving }) => {
  const isEdit = Boolean(initialCustomer?._id);
  
  const [form, setForm] = useState(() => ({
    name: initialCustomer?.name || initialName || "",
    customerType: initialCustomer?.customerType || "pos",
    email: initialCustomer?.email || "",
    phone: initialCustomer?.phone || "",
    mobile: initialCustomer?.mobile || "",
    address: initialCustomer?.address || "",
    city: initialCustomer?.city || "",
    tin: initialCustomer?.tin || "",
  }));

  const [error, setError] = useState("");
  const [fieldError, setFieldError] = useState({ field: "", message: "" });

  const tinDigits = useMemo(() => onlyDigits(form.tin), [form.tin]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'tin') {
      const rawValue = onlyDigits(value);
      setForm((prev) => ({ ...prev, [name]: rawValue }));
      if (fieldError.field === "tin") {
        setFieldError({ field: "", message: "" });
        setError("");
      }
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const validate = () => {
    if (!form.name.trim()) {
      return "Customer name is required";
    }

    if (tinDigits.length > 0 && tinDigits.length > 12) {
      return "TIN too long. Please use 10-12 digits.";
    }

    return "";
  };

  const handleSubmit = async () => {
    setError("");
    setFieldError({ field: "", message: "" });

    const msg = validate();
    if (msg) {
      setError(msg);
      if (msg.includes("TIN")) {
        setFieldError({ field: "tin", message: msg });
      } else if (msg.includes("name")) {
        setFieldError({ field: "name", message: msg });
      }
      return;
    }

    try {
      await onSave({
        ...form,
        tin: tinDigits || undefined,
      });
    } catch (err) {
      const errMsg = err?.response?.data?.message || err?.message || (typeof err === "string" ? err : null) || "Failed to save customer";
      setError(errMsg);
    }
  };

  return (
    <div
      className="space-y-4"
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          e.stopPropagation();
          handleSubmit();
        }
      }}
    >
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* BASIC INFO */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Customer Name *</label>
          <input
            type="text"
            name="name"
            value={form.name}
            onChange={handleChange}
            required
            placeholder="Enter customer name"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:bg-gray-100"
            disabled={saving}
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Customer Type</label>
          <select
            name="customerType"
            value={form.customerType}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:bg-gray-100"
            disabled={saving}
          >
            <option value="pos">POS Customer</option>
            <option value="accounting">Accounting</option>
            <option value="trucking">Trucking</option>
            <option value="freight_forwarding">Freight Forwarding</option>
          </select>
        </div>
      </div>

      {/* TIN FIELD */}
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">TIN (optional)</label>
        <input
          type="text"
          name="tin"
          value={formatTinDisplay(form.tin)}
          onChange={handleChange}
          placeholder="000-000-000-000"
          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:bg-gray-100 ${
            fieldError.field === "tin" ? "border-red-500 bg-red-50" : "border-gray-300"
          }`}
          disabled={saving}
        />
        <div className="text-[11px] text-gray-500 mt-1">
          Digits detected: {tinDigits.length || 0}
        </div>
        {fieldError.field === "tin" && (
          <p className="text-xs text-red-600 mt-1 font-semibold">✕ {fieldError.message}</p>
        )}
      </div>

      {/* CONTACT INFO */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Email</label>
          <input
            type="email"
            name="email"
            value={form.email}
            onChange={handleChange}
            placeholder="email@example.com"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:bg-gray-100"
            disabled={saving}
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Phone (Landline)</label>
          <input
            type="tel"
            name="phone"
            value={form.phone}
            onChange={handleChange}
            placeholder="(02) 1234-5678"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:bg-gray-100"
            disabled={saving}
          />
        </div>
      </div>

      {/* MOBILE & CITY */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Mobile</label>
          <input
            type="tel"
            name="mobile"
            value={form.mobile}
            onChange={handleChange}
            placeholder="09XXXXXXXXX"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:bg-gray-100"
            disabled={saving}
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">City</label>
          <input
            type="text"
            name="city"
            value={form.city}
            onChange={handleChange}
            placeholder="Manila"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:bg-gray-100"
            disabled={saving}
          />
        </div>
      </div>

      {/* ADDRESS */}
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">Address</label>
        <textarea
          name="address"
          value={form.address}
          onChange={handleChange}
          placeholder="Enter full address"
          rows={2}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:bg-gray-100"
          disabled={saving}
        />
      </div>

      {fieldError.field === "name" && fieldError.message && (
        <p className="text-xs text-red-600 font-semibold">✕ {fieldError.message}</p>
      )}

      {/* BUTTONS */}
      <div className="flex justify-end gap-2 pt-4 border-t">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition text-sm"
          disabled={saving}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          className="px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 transition text-sm disabled:opacity-50"
          disabled={saving}
        >
          {saving ? "Saving..." : "Save Customer"}
        </button>
      </div>
    </div>
  );
};

export default CustomerModal;
